import * as Sentry from "@sentry/react";
import { useArchiveThread, useUnarchiveThread } from "@/hooks/useThreads.tsx";
import { archiveToast, confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { useCallback } from "react";
import { toast } from "sonner";

/**
 * Canonical "mark a thread done" action: archive it, surface an Undo toast, and
 * notify the caller. The single source of truth shared by every surface that
 * archives a thread — the home/list rows, the calendar side-panel detail, the
 * right sidebar, and the ⌘D / `e` shortcuts — so they all behave identically
 * (same optimistic archive + same Undo affordance).
 *
 * Returns the underlying mutations too, so a host can read `isPending` for a
 * button spinner or wire the unarchive direction without re-instantiating them.
 */
export const useMarkThreadDone = ({
  threadId,
  onArchive,
}: {
  threadId: number;
  onArchive?: () => void;
}) => {
  const archiveMutation = useArchiveThread({ threadId });
  const unarchiveMutation = useUnarchiveThread({ threadId });
  // Depend on the stable `mutateAsync` methods, not the mutation objects — the
  // objects change identity on every state transition (idle → pending → …),
  // which would churn `markDone` and re-register every dependent shortcut.
  const { mutateAsync: archiveAsync } = archiveMutation;
  const { mutateAsync: unarchiveAsync } = unarchiveMutation;

  const markDone = useCallback(async () => {
    try {
      await archiveAsync();
      archiveToast(undefined, {
        actionText: "Undo",
        onAction: async (toastId) => {
          toast.dismiss(toastId);
          try {
            // The captured id, not the mount's: a list mounts this hook against
            // the focused row, and focus moves while this toast is still up.
            await unarchiveAsync(threadId);
          } catch (error) {
            Sentry.captureException(error, {
              tags: { component: "useMarkThreadDone", operation: "undoArchive" },
              extra: { threadId },
            });
            errorToast("Failed to undo");
            return;
          }
          // Outside the try, so a throw from the toast itself cannot report an
          // undo that landed as a failure.
          confirmationToast("Unarchived");
        },
      });
      onArchive?.();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "useMarkThreadDone", operation: "archiveThread" },
        extra: { threadId },
      });
      errorToast("Failed to archive thread");
    }
  }, [archiveAsync, unarchiveAsync, onArchive, threadId]);

  return { markDone, archiveMutation, unarchiveMutation };
};
